import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import { IColumnConfigTableResponsive } from 'src/app/domain/IColumnConfigTableResponsive';
import { FieldTypeEnum } from 'src/app/domain/registration/enums/field-type.enum';
import { TableResponsiveTypeColumnEnum } from 'src/app/domain/registration/enums/table-responsive-type-column.enum';
import { TypeIndexEnum } from 'src/app/domain/registration/enums/type-index.enum';
import { AbstractCrudIndex } from 'src/app/shared/components/crud/abstract-crud-index';
import { PermissionService } from 'src/app/shared/permission.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ContactsService } from 'src/app/views/contacts/contacts.service';
import { CustomerService } from 'src/app/views/registration/customers/crud-service';
import { PartnerService } from 'src/app/views/registration/partners/crud-service';
import { PeopleMultiSelectService } from './people-service';

export enum PeopleType {
  CUSTOMERS = 'customers',
  PARTNERS = 'partners',
  CONTACTS = 'contacts',
  ALL = 'all'
}

// Configuração de colunas para o componente
const PEOPLE_TABLE_CONFIG_COLUMNS: IColumnConfigTableResponsive[] = [
  {
    field_type: FieldTypeEnum.IMAGE,
    link_show: true,
    property: 'image',
    display: 'IMAGEM',
    class: TableResponsiveTypeColumnEnum.SUBTITLE,
  },
  {
    field_type: FieldTypeEnum.LABEL,
    link_show: true,
    property: 'document',
    display: 'DOCUMENTO',
    class: TableResponsiveTypeColumnEnum.SUBTITLE,
  },
  {
    field_type: FieldTypeEnum.LABEL,
    link_show: true,
    property: 'name',
    display: 'NOME',
    class: TableResponsiveTypeColumnEnum.SUBTITLE,
    maxLength: 25
  },
  {
    field_type: FieldTypeEnum.LABEL,
    link_show: true,
    property: 'email',
    display: 'E-MAIL',
    class: TableResponsiveTypeColumnEnum.SUBTITLE,
  },
  {
    field_type: FieldTypeEnum.LABEL,
    link_show: true,
    property: 'phone',
    display: 'TELEFONE',
    class: TableResponsiveTypeColumnEnum.SUBTITLE,
  },
  {
    field_type: FieldTypeEnum.LABEL,
    link_show: true,
    property: 'type',
    display: 'TIPO',
    class: TableResponsiveTypeColumnEnum.SUBTITLE,
  }
];

@Component({
  selector: 'app-people-index-select',
  templateUrl: './people-index-select.component.html',
  styleUrls: ['./people-index-select.component.sass'],
})
export class PeopleIndexSelectComponent extends AbstractCrudIndex<PeopleMultiSelectService> implements OnInit {
  override title: string = 'SELECIONE UMA PESSOA';
  override COLUMNS_TO_SHOW_WEB = [
    'image',
    'document',
    'name',
    'type',
    'email',
    'phone'
  ];

  // Configuração de colunas para o componente
  override configColumns = PEOPLE_TABLE_CONFIG_COLUMNS;

  // Propriedade displayedColumns para o template
  override displayedColumns = this.COLUMNS_TO_SHOW_WEB;

  peopleType: PeopleType = PeopleType.ALL;
  currentService: any;
  override dataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);
  override length: number = 0;
  override params: any = { limit: 25, page: 0, filter: '' };

  constructor(
    override dialog: MatDialog,
    override service: PeopleMultiSelectService,
    override toastService: ToastService,
    override mediaObserver: MediaObserver,
    public dialogRefSelect: MatDialogRef<PeopleIndexSelectComponent>,
    private fb: FormBuilder,
    protected cdr: ChangeDetectorRef,
    protected authService: AuthService,
    private permissionService: PermissionService,
    override router: Router,
    private customerService: CustomerService,
    private partnerService: PartnerService,
    private contactsService: ContactsService,
    @Inject(MAT_DIALOG_DATA)
    data: {
      type: TypeIndexEnum;
      select: boolean;
      params: any;
      peopleType: PeopleType;
    }
  ) {
    super(dialog, service, toastService, mediaObserver, router);
    this.dataSelect = data;
    this.params = { ...this.params, ...data.params };
    this.peopleType = data.peopleType || PeopleType.ALL;
    this.setupService();
  }

  ngOnInit(): void {
    this.loadList();
  }

  private setupService(): void {
    switch (this.peopleType) {
      case PeopleType.CUSTOMERS:
        this.currentService = this.customerService;
        this.title = 'SELECIONE UM CLIENTE';
        break;
      case PeopleType.PARTNERS:
        this.currentService = this.partnerService;
        this.title = 'SELECIONE UM PARCEIRO';
        break;
      case PeopleType.CONTACTS:
        this.currentService = this.contactsService;
        this.title = 'SELECIONE UM CONTATO';
        break;
      default:
        this.title = 'SELECIONE UMA PESSOA';
        break;
    }
  }

  override loadList(): void {
    // Buscar todos os tipos de pessoas
    const customersPromise = this.customerService.listAll(this.params).pipe(take(1)).toPromise();
    const partnersPromise = this.partnerService.listAll(this.params).pipe(take(1)).toPromise();
    const contactsPromise = this.contactsService.listAll(this.params).pipe(take(1)).toPromise();

    // Combinar todas as promessas
    Promise.all([customersPromise, partnersPromise, contactsPromise])
      .then(([customersResponse, partnersResponse, contactsResponse]) => {
        let allPeople: any[] = [];
        let totalCount = 0;

        // Processar clientes
        if (customersResponse && Array.isArray(customersResponse) && customersResponse.length >= 2) {
          const customers = customersResponse[0] || [];
          allPeople.push(...customers.map((item: any) => ({
            ...this.normalizeItem(item),
            type: 'Cliente',
            typeId: 'customer'
          })));
          totalCount += customersResponse[1] || 0;
        }

        // Processar parceiros
        if (partnersResponse && Array.isArray(partnersResponse) && partnersResponse.length >= 2) {
          const partners = partnersResponse[0] || [];
          allPeople.push(...partners.map((item: any) => ({
            ...this.normalizeItem(item),
            type: 'Parceiro',
            typeId: 'partner'
          })));
          totalCount += partnersResponse[1] || 0;
        }

        // Processar contatos
        if (contactsResponse && Array.isArray(contactsResponse) && contactsResponse.length >= 2) {
          const contacts = contactsResponse[0] || [];
          allPeople.push(...contacts.map((item: any) => ({
            ...this.normalizeItem(item),
            type: 'Contato',
            typeId: 'contact'
          })));
          totalCount += contactsResponse[1] || 0;
        }

        this.length = totalCount;
        this.dataSource = new MatTableDataSource<any>(allPeople);
        this.cdr.detectChanges();
      })
      .catch((error: any) => {
        console.error('ERROR', error);
        this.toastService.showError('Erro ao buscar dados. Tente novamente.');
        this.dataSource = new MatTableDataSource<any>([]);
        this.length = 0;
      });
  }

  private normalizeItem(item: any): any {
    const normalized = { ...item };

    // Normalizar nome
    normalized.name = item.name || item.companyName || item.tradingName || '';

    // Normalizar documento baseado no legal_personality
    // Aceita tanto string quanto number para compatibilidade
    const legalPersonality = item.legalPersonality;
    const isPessoaFisica = legalPersonality === 1 ||
      legalPersonality === '1' ||
      legalPersonality === 'Pessoa Física';

    if (isPessoaFisica) {
      // Para pessoa física, priorizar CPF
      normalized.document = item.cpf || item.document || '';
    } else {
      // Para pessoa jurídica, priorizar CNPJ
      normalized.document = item.cnpj || item.document || '';
    }

    // Normalizar email
    normalized.email = item.email || '';

    // Normalizar telefone
    normalized.phone = item.phone || item.cellPhone || '';

    // Normalizar imagem
    normalized.image = item.image || '';

    return normalized;
  }

  override submitAdvancedFilter(filterForm: FormGroup) {
    const filterData = filterForm.value;

    // Usar o método listAll do serviço que já tem a lógica de filtro
    const customersPromise = this.customerService.listAll({ ...this.params, ...filterData }).pipe(take(1)).toPromise();
    const partnersPromise = this.partnerService.listAll({ ...this.params, ...filterData }).pipe(take(1)).toPromise();
    const contactsPromise = this.contactsService.listAll({ ...this.params, ...filterData }).pipe(take(1)).toPromise();

    // Combinar todas as promessas
    Promise.all([customersPromise, partnersPromise, contactsPromise])
      .then(([customersResponse, partnersResponse, contactsResponse]) => {
        let allPeople: any[] = [];
        let totalCount = 0;

        // Processar clientes
        if (customersResponse && Array.isArray(customersResponse) && customersResponse.length >= 2) {
          const customers = customersResponse[0] || [];
          allPeople.push(...customers.map((item: any) => ({
            ...this.normalizeItem(item),
            type: 'Cliente',
            typeId: 'customer'
          })));
          totalCount += customersResponse[1] || 0;
        }

        // Processar parceiros
        if (partnersResponse && Array.isArray(partnersResponse) && partnersResponse.length >= 2) {
          const partners = partnersResponse[0] || [];
          allPeople.push(...partners.map((item: any) => ({
            ...this.normalizeItem(item),
            type: 'Parceiro',
            typeId: 'partner'
          })));
          totalCount += partnersResponse[1] || 0;
        }

        // Processar contatos
        if (contactsResponse && Array.isArray(contactsResponse) && contactsResponse.length >= 2) {
          const contacts = contactsResponse[0] || [];
          allPeople.push(...contacts.map((item: any) => ({
            ...this.normalizeItem(item),
            type: 'Contato',
            typeId: 'contact'
          })));
          totalCount += contactsResponse[1] || 0;
        }

        this.length = totalCount;
        this.dataSource = new MatTableDataSource<any>(allPeople);
        this.cdr.detectChanges();
      })
      .catch((error: any) => {
        console.error('ERROR', error);
        this.toastService.showError('Erro ao buscar dados. Tente novamente.');
        this.dataSource = new MatTableDataSource<any>([]);
        this.length = 0;
      });
  }

  onCancel() {
    this.dialogRefSelect.close();
  }

  onSelect(data: any) {
    console.log('[PeopleIndexSelectComponent] onSelect chamado com dados:', data);
    // Retorna diretamente a pessoa selecionada (não um array)
    this.dialogRefSelect.close(data?.row);
  }

  override changePage(event: any) {
    this.params.page = event.pageIndex;
    this.params.limit = event.pageSize;
    this.loadList();
  }

  override filter(event: any) {
    this.params.filter = event;
    this.params.page = 0;
    this.loadList();
  }

  _openShow(event: any) {
    // Implementar se necessário
  }

  _openEdit(event: any) {
    // Implementar se necessário
  }

  _openDelete(event: any) {
    // Implementar se necessário
  }
} 